/* Imagery (site media + social links) admin panel — extracted from Admin.jsx tech-debt split. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { UploadSimple, X as XIcon } from "@phosphor-icons/react";
import Media from "@/components/Media";

const MAX_UPLOAD_MB = 10;
const ACCEPT_MEDIA = "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime";

export default function ImageryPanel() {
  const [settings, setSettings] = useState(null);
  const [uploadingSettings, setUploadingSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const saveSocialSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {};
      for (const k of ["instagram_url", "tiktok_url", "youtube_url", "pinterest_url", "facebook_url", "contact_email"]) {
        if (settings[k] !== undefined) payload[k] = (settings[k] || "").trim();
      }
      await api.patch("/settings", payload);
      toast.success("Social links saved");
    } catch { toast.error("Could not save"); }
    finally { setSavingSettings(false); }
  };

  const uploadSettingsImage = async (field, file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`File is too large — please keep uploads under ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    setUploadingSettings((s) => ({ ...s, [field]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...settings, [field]: data.url };
      setSettings(next);
      await api.patch("/settings", { [field]: data.url });
      toast.success("Saved");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingSettings((s) => ({ ...s, [field]: false })); }
  };

  const clearSettingsImage = async (field) => {
    setSettings((s) => ({ ...s, [field]: null }));
    await api.patch("/settings", { [field]: null });
    toast.success("Cleared");
  };

  if (!settings) return null;

  return (
    <div className="space-y-6 max-w-4xl">
      <p className="text-rf-ink-soft">These images and links appear sitewide. Changes are live the next time a visitor loads the page — no redeploy needed.</p>

      <div className="card-recipe">
        <p className="overline mb-2">Social &amp; contact links</p>
        <p className="text-sm text-rf-ink-soft mb-4">These power the footer icons. Leave blank to fall back to the default <code className="text-xs">@recipefiler</code> URLs.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { field: "instagram_url", label: "Instagram URL", placeholder: "https://instagram.com/recipefiler" },
            { field: "tiktok_url", label: "TikTok URL", placeholder: "https://tiktok.com/@recipefiler" },
            { field: "youtube_url", label: "YouTube URL", placeholder: "https://www.youtube.com/@recipefiler" },
            { field: "pinterest_url", label: "Pinterest URL", placeholder: "https://uk.pinterest.com/recipefiler" },
            { field: "facebook_url", label: "Facebook URL", placeholder: "https://www.facebook.com/people/RecipeFiler/61591833807686/" },
            { field: "contact_email", label: "Contact email", placeholder: "contactus@recipefiler.com" },
          ].map(({ field, label, placeholder }) => (
            <div key={field}>
              <label className="text-xs font-bold text-rf-ink-soft uppercase tracking-wider">{label}</label>
              <input
                data-testid={`settings-${field}`}
                type="text"
                value={settings[field] || ""}
                onChange={(e) => setSettings({ ...settings, [field]: e.target.value })}
                placeholder={placeholder}
                className="input-rf !py-2.5 mt-1 w-full"
              />
            </div>
          ))}
        </div>
        <button
          data-testid="settings-save-social"
          onClick={saveSocialSettings}
          disabled={savingSettings}
          className="btn-amber mt-4 !py-2.5 text-sm"
        >
          {savingSettings ? "Saving…" : "Save social links"}
        </button>
      </div>

      {[
        { field: "about_image_url", label: "About / secondary marketing media", hint: "Used in marketing sections that read this setting. Image or short looping video both work.", aspect: "16/9", allowVideo: true },
        { field: "brand_logo_url", label: "Brand logo (header)", hint: "Replaces the default ForkKnife icon in the header. Use a transparent PNG or SVG, ~80×80.", aspect: "1/1", allowVideo: false },
        { field: "favicon_url", label: "Favicon", hint: "Browser-tab icon. Use a square PNG, 256×256.", aspect: "1/1", allowVideo: false },
      ].map(({ field, label, hint, aspect, allowVideo }) => (
        <div key={field} className="card-recipe">
          <div className="flex flex-wrap items-start gap-6">
            <div className="shrink-0" style={{ width: 140 }}>
              {settings[field] ? (
                <div className="rounded-xl overflow-hidden border border-rf-ink/10" style={{ aspectRatio: aspect }}>
                  <Media src={settings[field]} alt={label} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="rounded-xl bg-rf-cream-alt border border-dashed border-rf-ink/20 flex items-center justify-center text-rf-ink-soft text-xs" style={{ aspectRatio: aspect }}>No media</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-extrabold text-xl text-rf-ink mb-1">{label}</p>
              <p className="text-sm text-rf-ink-soft mb-3">{hint}</p>
              <div className="flex flex-wrap gap-2 items-center">
                <label className="btn-outline-ink !py-2.5 !px-4 !text-sm cursor-pointer">
                  <UploadSimple size={16} /> {uploadingSettings[field] ? "Uploading…" : (settings[field] ? "Replace" : "Upload")}
                  <input
                    type="file"
                    accept={field === "favicon_url"
                      ? "image/png,image/x-icon,image/svg+xml"
                      : (allowVideo ? `${ACCEPT_MEDIA},image/svg+xml` : "image/jpeg,image/png,image/webp,image/svg+xml")}
                    className="hidden"
                    onChange={(e) => uploadSettingsImage(field, e.target.files?.[0])}
                    data-testid={`imagery-${field}`}
                  />
                </label>
                {settings[field] && (
                  <button onClick={() => clearSettingsImage(field)} className="text-xs font-bold text-red-600 hover:underline inline-flex items-center gap-1"><XIcon size={14} /> Remove</button>
                )}
                {settings[field] && (
                  <span className="font-mono text-xs text-rf-ink-soft truncate max-w-xs">{settings[field]}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
